import { useCallback, useEffect, useRef, useState } from 'react';

const VIOLATION_LIMIT = 5;

export type ViolationType =
  | 'tabSwitch'
  | 'windowBlur'
  | 'fullscreenExit'
  | 'copyPaste'
  | 'rightClick';

export interface UseExamSecurityOptions {
  /** Called when any violation occurs (use to show warning modal). Receives current violation count. */
  onViolation?: (violationCount: number, type: ViolationType) => void;
  /** Called when violation count reaches or exceeds the limit (e.g. auto-submit exam). */
  onViolationLimitExceeded?: () => void;
  /** Maximum violations before triggering limit exceeded. Default: 3 */
  violationLimit?: number;
  /** If true, security listeners are active. Default: true */
  enabled?: boolean;
}

export interface UseExamSecurityReturn {
  /** Current number of violations. */
  violations: number;
  /** Maximum allowed violations before auto-submit. */
  violationLimit: number;
  /** Reset violation count to 0. */
  resetViolations: () => void;
}

/**
 * Hook to enforce exam security: detects tab switch (visibility / hidden tab), fullscreen exit,
 * copy/paste, and right-click. Tracks violation count; when it exceeds the limit,
 * calls onViolationLimitExceeded (e.g. auto-submit exam).
 */
export function useExamSecurity({
  onViolation,
  onViolationLimitExceeded,
  violationLimit = VIOLATION_LIMIT,
  enabled = true,
}: UseExamSecurityOptions = {}): UseExamSecurityReturn {
  const [violations, setViolations] = useState(0);
  const onViolationRef = useRef(onViolation);
  const onLimitExceededRef = useRef(onViolationLimitExceeded);

  onViolationRef.current = onViolation;
  onLimitExceededRef.current = onViolationLimitExceeded;

  // Prevent double-counting copy/paste bursts and duplicate events for the same action.
  const lastViolationTimeRef = useRef(0);
  const DEDUPE_WINDOW_MS = 300;

  /** True while the exam tab is away; reset when the user returns (visible). */
  const isAwayRef = useRef<boolean>(
    typeof document !== 'undefined' ? Boolean(document.hidden) : false
  );

  const handleViolation = useCallback(
    (type: ViolationType) => {
      const now = Date.now();
      if (now - lastViolationTimeRef.current < DEDUPE_WINDOW_MS) return;
      lastViolationTimeRef.current = now;

      setViolations((prev) => {
        const next = prev + 1;
        onViolationRef.current?.(next, type);
        if (next >= violationLimit) {
          onLimitExceededRef.current?.();
        }
        return next;
      });
    },
    [violationLimit]
  );

  useEffect(() => {
    if (!enabled) return;

    const markReturned = () => {
      isAwayRef.current = false;
    };

    const markTabSwitchAway = () => {
      if (isAwayRef.current) return;
      isAwayRef.current = true;
      handleViolation('tabSwitch');
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        markTabSwitchAway();
      } else {
        markReturned();
      }
    };

    // `blur` fires on mobile for in-app taps while the tab stays visible; ignore it for tab switches.
    // Desktop alt-tab may blur before `visibilitychange`; defer and only count if the tab is hidden.
    const handleBlur = () => {
      window.setTimeout(() => {
        if (document.hidden) markTabSwitchAway();
      }, 200);
    };

    const handleFullscreenChange = () => {
      if (!document.fullscreenElement) {
        handleViolation('fullscreenExit');
      }
    };

    const handleCopyPaste = (e: Event) => {
      e.preventDefault();
      handleViolation('copyPaste');
    };

    const handleRightClick = (e: Event) => {
      e.preventDefault();
      handleViolation('rightClick');
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleBlur);
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('copy', handleCopyPaste);
    document.addEventListener('paste', handleCopyPaste);
    document.addEventListener('cut', handleCopyPaste);
    document.addEventListener('contextmenu', handleRightClick);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleBlur);
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('copy', handleCopyPaste);
      document.removeEventListener('paste', handleCopyPaste);
      document.removeEventListener('cut', handleCopyPaste);
      document.removeEventListener('contextmenu', handleRightClick);
    };
  }, [enabled, handleViolation]);

  const resetViolations = useCallback(() => setViolations(0), []);

  return {
    violations,
    violationLimit,
    resetViolations,
  };
}

export default useExamSecurity;
